import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useUser } from "../context/AuthContext";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import Logo from "../assets/lazerzonelogo.png";
import profile_icon from "../assets/profile-user.webp";
import "./Navbar.css";

const Navbar = () => {
  const { isLoggedIn, logout, user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = React.useState(false);
  const menuRef = useRef(null);
  const profileRef = useRef(null);

  const navLinks = [
    { name: "Home", href: "/" },
    { name: "About", href: "/about" },
    { name: "Services", href: "/services" },
    { name: "Pricing", href: "/pricing" },
    { name: "Contact", href: "/contact" },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/");
    setIsProfileMenuOpen(false);
  };

  if (location.pathname === "/auth") {
    return null;
  }

  return (
    <nav
      className="w-full top-0 left-0 z-50 backdrop-blur-sm shadow-lg sticky"
      style={{
        backgroundColor: "#1F2937", // Dark navy for navbar background
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div className="w-full flex flex-nowrap items-center justify-between mx-auto px-4 py-2">
        {/* Logo y botón móvil */}
        <div className="flex items-center space-x-2 rtl:space-x-reverse">
          <button
            type="button"
            className="inline-flex items-center p-1.5 w-9 h-9 justify-center text-[#E9EAE8] rounded-lg md:hidden hover:bg-[#6A706C] focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span className="sr-only">Open main menu</span>
            {isMenuOpen ? (
              <XMarkIcon className="w-5 h-5 text-[#E9EAE8]" />
            ) : (
              <Bars3Icon className="w-5 h-5 text-[#E9EAE8]" />
            )}
          </button>

          <Link to="/" className="flex items-center">
            <img src={Logo} className="h-12" alt="Logo" />
          </Link>
        </div>

        {/* Menú principal */}
        <div
          ref={menuRef}
          className={`${
            isMenuOpen ? "block" : "hidden"
          } w-full md:flex md:w-auto md:order-1 h-full`}
        >
          {navLinks.map((item) => (
            <li key={item.name} className="flex items-center">
              <Link
                to={item.href}
                className={`block px-3 py-1.5 rounded md:p-0 transition-all duration-300 text-lg no-underline group ${
                  location.pathname === item.href
                    ? "text-[#566D87] border-b-2 border-[#566D87] md:border-transparent md:pb-1 md:hover:border-[#566D87]/80"
                    : "text-[#E9EAE8] hover:text-[#566D87] hover:bg-[#6A706C]/30 md:hover:bg-transparent"
                }`}
              >
                <span className="md:hidden">{item.name}</span>
                <span className="hidden md:inline-block relative">
                  {item.name}
                  <span
                    className={`absolute left-0 -bottom-1 h-[2px] w-0 transition-all duration-300 ${
                      location.pathname === item.href
                        ? "bg-[#566D87] w-full"
                        : "group-hover:w-full bg-[#566D87]/70"
                    }`}
                  ></span>
                </span>
              </Link>
            </li>
          ))}
        </div>

        {/* Menú de usuario */}
        <div ref={profileRef} className="flex items-center md:order-2">
          {isLoggedIn ? (
            <div className="relative">
              <button
                type="button"
                className="flex p-1 text-sm rounded-full focus:ring-4 focus:ring-[#6A706C] transition-all"
                style={{ backgroundColor: "#566D87" }} // Blue-gray for profile button
                onClick={() => setIsProfileMenuOpen(!isProfileMenuOpen)}
              >
                <img
                  className="w-8 h-8 rounded-full border-[#6A706C] hover:border-[#E9EAE8]"
                  src={profile_icon}
                  alt={user?.name || "User"}
                />
              </button>

              {isProfileMenuOpen && (
                <div className="z-50 absolute right-0 mt-2 w-56 bg-[#1F2937] divide-y divide-[#6A706C] rounded-lg shadow-xl backdrop-blur-sm">
                  <div className="px-4 py-2">
                    <span className="block text-sm text-[#E9EAE8] font-medium">
                      {user?.name || "User"}
                    </span>
                    <span className="block text-xs text-[#6A706C] truncate">
                      {user?.email || "user@example.com"}
                    </span>
                  </div>
                  <ul className="py-2">
                    <li>
                      <Link
                        to="/profile"
                        className="block px-4 py-2 text-sm text-[#E9EAE8] hover:bg-[#6A706C]/50 transition-colors no-underline"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Profile
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/booking-history"
                        className="block px-4 py-2 text-sm text-[#E9EAE8] hover:bg-[#6A706C]/50 transition-colors no-underline"
                        onClick={() => setIsProfileMenuOpen(false)}
                      >
                        Booking History
                      </Link>
                    </li>
                    <li>
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-[#E9EAE8] hover:bg-[#6A706C]/50 transition-colors"
                      >
                        Sign out
                      </button>
                    </li>
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <Link
              to="/auth"
              className={`ml-2 font-medium rounded-lg text-sm px-3 py-1.5 text-center transition-all duration-300 no-underline focus:ring-4 focus:ring-[#6A706C] ${
                location.pathname === "/auth"
                  ? "text-[#566D87] border-b-2 border-[#566D87] bg-transparent"
                  : "text-[#141318] bg-[#E9EAE8] hover:bg-[#6A706C]"
              }`}
            >
              <span className="md:hidden">Sign In/Up</span>
              <span className="hidden md:inline-block relative">
                Sign In/Up
                {location.pathname !== "/auth" && (
                  <span className="absolute left-0 -bottom-1 h-[2px] w-full bg-[#566D87]/50"></span>
                )}
              </span>
            </Link>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;